import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import dayjs from 'dayjs';
import jalaliday from 'jalaliday';
import api from 'utils/api';

dayjs.extend(jalaliday);

const AvailabilityCalendar = ({ selectedSalon }) => {
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedWeek, setSelectedWeek] = useState(0);

  const [bookings, setBookings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const salonId = selectedSalon || '68c806bf374b8c596edb208c';

  const getCurrentWeekDates = () => {
    const today = new Date();
    const currentDay = today.getDay(); // Sunday = 0
    const saturday = new Date(today);
    saturday.setDate(today.getDate() - ((currentDay + 1) % 7));

    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(saturday);
      date.setDate(saturday.getDate() + i + selectedWeek * 7);
      weekDates.push(date);
    }
    return weekDates;
  };

  const weekDates = getCurrentWeekDates();

  const persianDays = [
    'شنبه',
    'یکشنبه',
    'دوشنبه',
    'سه‌شنبه',
    'چهارشنبه',
    'پنج‌شنبه',
    'جمعه',
  ];

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/salons/${salonId}/employees`);
        const employeeList = Array.isArray(res.data) ? res.data : [];
        setEmployees([
          { label: 'همه', value: 'all' },
          ...employeeList.map((emp) => ({ ...emp, label: emp.name, value: emp._id })),
        ]);
      } catch (err) {
        console.error('خطا در دریافت کارمندان:', err);
        setEmployees([{ label: 'همه', value: 'all' }]);
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, [salonId]);

  // Fetch bookings (availabilities)
  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setLoading(true);
        console.log('نوبت‌ها دریافت شو...');

        let url;
        if (selectedEmployee === 'all') {
          url = `/salons/${salonId}/bookings`;
        } else {
          url = `/salons/${salonId}/employees/${selectedEmployee}/bookings`;
        }

        const res = await api.get(url);
        const bookingList = Array.isArray(res.data) ? res.data : [];
        console.log('نوبت‌ها:', bookingList);
        setBookings(bookingList);
      } catch (err) {
        console.error('خطا در دریافت نوبت‌ها:', err);
        setBookings([]);
      } finally {
        setLoading(false);
      }
    };

    if (salonId) {
      fetchBookings();
    }
  }, [selectedEmployee, salonId]);

  const formatDate = (date) =>
    dayjs(date).calendar('jalali').locale('fa').format('D MMM');

  const getEmployeeId = (employee) => {
    if (!employee) return null;
    return typeof employee === 'string'
      ? employee
      : employee._id || employee.$oid;
  };

  const getBookingsForDate = (date, empId) => {
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);

    return bookings.filter((b) => {
      const start = new Date(b.start);
      const employeeId = getEmployeeId(b.employee);
      return start >= dayStart && start <= dayEnd && employeeId === empId;
    });
  };

  const getSlotColor = (status) => {
    switch (status) {
      case 'confirmed':
      case 'paid':
        return 'bg-success/20 text-success border-success/30';
      case 'pending':
        return 'bg-warning/20 text-warning border-warning/30';
      case 'cancelled':
      case 'expired':
        return 'bg-error/20 text-error border-error/30';
      default:
        return 'bg-muted/20 text-muted-foreground';
    }
  };

  const getReserverStatus = (status) => {
    switch (status) {
      case 'pending':
        return 'بررسی نشده';
      case 'confirmed':
        return 'تایید شده';
      case 'paid':
        return 'پرداخت شده';
      case 'cancelled':
        return 'کنسل شده';
      case 'expired':
        return 'منقضی شده';
      case 'done':
        return 'انجام شده';
      default:
        return status;
    }
  };

  const handleSlotClick = (slot) => {
    setSelectedSlot(slot);
    setShowModal(true);
  };

  const handleStatusChange = async (newStatus) => {
    console.log('newStatus', newStatus);
    try {
      const res = await api.patch(
        `salons/${salonId}/bookings/${selectedSlot._id}/updatestatus`,
        {
          status: newStatus,
        }
      );
      setBookings((prev) =>
        prev.map((b) =>
          b._id === selectedSlot._id ? { ...b, status: newStatus } : b
        )
      );
      setSelectedSlot({ ...selectedSlot, status: newStatus });
    } catch (err) {
      console.error('Failed to update booking status:', err);
    }
  };

  const shownEmployees =
    selectedEmployee === 'all'
      ? employees.filter((e) => e.value !== 'all')
      : employees.filter((e) => e.value === selectedEmployee);

  const scheduleDayKeys = ['saturday', 'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  const getShiftForDay = (employee, dayIndex) => {
    const dayKey = scheduleDayKeys[dayIndex];
    return (employee.workSchedule || []).find(
      (shift) => String(shift.day).toLowerCase() === dayKey
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            نوبت‌ها و رزروها
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            مشاهده وضعیت تمامی بازه‌ها، مشتریان و خدمات
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 max-w-xs">
          <Select
            label="انتخاب کارمند"
            options={employees}
            value={selectedEmployee}
            onChange={setSelectedEmployee}
          />
        </div>

        <div className="flex items-end gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setSelectedWeek(selectedWeek - 1)}
            iconName="ChevronRight"
            iconSize={16}
          />
          <div className="px-4 py-2 text-sm text-foreground bg-muted rounded-md min-w-[120px] text-center">
            هفته{' '}
            {selectedWeek === 0
              ? 'جاری'
              : selectedWeek > 0
              ? `+${selectedWeek}`
              : selectedWeek}
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setSelectedWeek(selectedWeek + 1)}
            iconName="ChevronLeft"
            iconSize={16}
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            {/* Header */}
            <div className="grid grid-cols-8 border-b border-border">
              <div className="p-4 bg-muted/50 text-sm font-medium text-foreground">
                کارمند
              </div>
              {persianDays.map((day, i) => (
                <div key={day} className="p-4 bg-muted/50 text-center">
                  <div className="text-sm font-medium text-foreground">
                    {day}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {formatDate(weekDates[i])}
                  </div>
                </div>
              ))}
            </div>

            {/* Rows */}
            {shownEmployees.map((emp) => (
              <div
                key={emp.value}
                className="grid grid-cols-8 border-b border-border">
                <div className="p-4 bg-muted/20 flex items-center">
                  <span className="text-sm font-medium text-foreground">
                    {emp.label}
                  </span>
                </div>

                {weekDates.map((date, i) => {
                  const slots = getBookingsForDate(date, emp.value);
                  const shift = getShiftForDay(emp, i);
                  return (
                    <div key={i} className={`p-2 ${shift ? 'bg-success/[0.04]' : 'bg-muted/[0.08]'}`}>
                      {shift && (
                        <div className="mb-2 rounded-md border border-success/20 bg-success/10 px-2 py-1.5 text-center">
                          <div className="text-[11px] font-semibold text-success">حضور پرسنل</div>
                          <div className="mt-1 text-[11px] text-foreground">{shift.startTime} تا {shift.endTime}</div>
                        </div>
                      )}
                      <div className="flex flex-col gap-1">
                        {slots.length > 0 ? (
                          slots.map((slot) => (
                            <button
                              key={slot._id}
                              onClick={() => handleSlotClick(slot)}
                              className={`w-full rounded-md border text-xs p-1 font-medium transition-colors duration-150 ${getSlotColor(
                                slot.status
                              )}`}>
                              {dayjs(slot.start)
                                .locale('fa')
                                .calendar('jalali')
                                .format('HH:mm')}{' '}
                              • {slot.clientName || '---'} <br />
                              {slot.service?.name || ''}
                              {slot.orderType == 'gift' ? '- سفارش هدیه  ' : ''}
                            </button>
                          ))
                        ) : (
                          <div className="text-xs text-muted-foreground text-center py-2">
                            {shift ? 'رزروی ثبت نشده' : 'روز کاری نیست'}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedSlot && (
        <div className="fixed inset-0 bg-black/50 z-modal flex items-center justify-center p-4">
          <div className="bg-popover border border-border rounded-lg w-full max-w-md p-6 space-y-3">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              جزئیات رزرو
            </h3>
            <div className="relative">
              <p className="absolute fixed left-0 bottom-0 bg-green-500 p-1 rounded-lg text-xs text-white">
                {' '}
                {selectedSlot.orderType == 'gift' ? 'سفارش هدیه' : ''}
              </p>
            </div>
            <p>کارمند: {selectedSlot.employee?.name}</p>
            <p>مشتری: {selectedSlot.clientName}</p>
            <p>سفارش: {selectedSlot.service?.name}</p>
            {/*  */}
            <p>وضعیت فعلی: {getReserverStatus(selectedSlot.status)}</p>
            <p>
              تاریخ:{' '}
              {dayjs(selectedSlot.start)
                .locale('fa')
                .calendar('jalali')
                .format('YYYY/MM/DD')}
            </p>
            <p>
              ساعت:{' '}
              {dayjs(selectedSlot.start)
                .locale('fa')
                .calendar('jalali')
                .format('HH:mm')}
            </p>

            {selectedSlot.receiptUrl && (
              <div className="mt-3">
                <p className="text-sm font-medium mb-1">رسید پرداخت:</p>
                <img
                  src={selectedSlot.receiptUrl}
                  alt="Receipt"
                  className="w-full rounded-md border"
                />
              </div>
            )}

            <div className="mt-4 flex gap-2 flex-wrap">
              {[
                'pending',
                'confirmed',
                'paid',
                'cancelled',
                'expired',
                'done',
              ].map((st) => (
                <Button
                  key={st}
                  size="sm"
                  variant={selectedSlot.status === st ? 'default' : 'outline'}
                  onClick={() => handleStatusChange(st)}>
                  {getReserverStatus(st)}
                </Button>
              ))}
            </div>

            <div className="mt-4">
              <Button variant="default" onClick={() => setShowModal(false)}>
                بستن
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvailabilityCalendar;
